import React, {useMemo} from 'react';
import cn from 'classnames';

import {isProductSearchHitModel} from 'coreClasses/search/ProductSearchHitModel';
import {useUI} from 'coreHooks';

import {PRODUCT_IMAGES_SETTINGS, ViewType} from 'brand/app/constants';
import Link from 'brandComponents/Link';
import Picture from 'brandComponents/Picture';

import {EProductMediaType, IProductMediaProps} from './IProductMedia';
import {ProductMediaCarousel} from './Subcomponents';

import styles from './ProductMedia.module.scss';

const ProductMedia = ({
	product,
	mediaType,
	onClick,
	classNames,
	carouselConfig,
	ratio = null,
	imageSettings = PRODUCT_IMAGES_SETTINGS.productTile,
	renderPicture,
	isLink = false,
	showPlaceholder,
	priority = false,
}: IProductMediaProps) => {
	const isServerUI = useUI('isServer');
	const isProductSearchHit = isProductSearchHitModel(product);

	const isCarousel = useMemo(() => mediaType === EProductMediaType.CAROUSEL, [mediaType]);

	const url = useMemo(
		() => (isProductSearchHit ? product.getUrl() : product.getProductUrl()),
		[isProductSearchHit, product],
	);

	const WrapperTag = useMemo(() => {
		if (onClick && !isLink) {
			return 'button';
		}

		return Link;
	}, [onClick, isLink]);

	const wrapperProps = useMemo(() => {
		return {
			className: cn(styles['product-media__link-image-wrapper'], 'product-media__image-wrapper'),
			to: isLink ? url : undefined,
			type: onClick ? 'button' : undefined,
			onClick,
		};
	}, [url, onClick, isLink]);

	const tileImage = useMemo(() => {
		return product.getProductImage({viewType: ViewType.ZOOM, transformationObject: imageSettings});
	}, [product, imageSettings]);

	const images = useMemo(() => {
		if (isProductSearchHit) {
			return [];
		}

		return product.getProductImages(ViewType.ZOOM, imageSettings);
	}, [product, isProductSearchHit]);

	const picture = useMemo(() => {
		if (typeof renderPicture === 'function') {
			return renderPicture();
		}

		return (
			<Picture
				image={tileImage}
				showSkeleton={!isServerUI}
				showPlaceholder={showPlaceholder}
				priority={priority}
				ratio={ratio}
				classNames={cn(styles['product-media__tile-image'], {
					[styles['product-media__tile-image--carousel']]: isCarousel && images.length > 0,
				})}
			/>
		);
	}, [isCarousel, renderPicture, tileImage, ratio, images, isServerUI, showPlaceholder, priority]);

	return (
		<div className={cn(styles['product-media'], 'product-media', classNames)}>
			<WrapperTag {...wrapperProps}>
				{/* @ts-ignore */}
				{picture}
			</WrapperTag>
			{isCarousel && images.length > 0 && (
				<div
					className={cn(styles['product-media__carousel-wrapper'], 'product-media-carousel')}
					id="product-media-carousel"
				>
					<ProductMediaCarousel
						onClick={onClick}
						carouselConfig={carouselConfig}
						ratio={ratio}
						url={url}
						productId={product.getProductId()}
						images={images}
						tileImageUrl={tileImage.getUrl()}
						showPlaceholder={showPlaceholder}
					/>
				</div>
			)}
		</div>
	);
};

export default ProductMedia;
